import React, { useEffect, useState } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from 'react-hot-toast';
import moment from 'moment';
import Select from 'react-select';

export default function HolidayDetailsList() {
    const { http, user } = AuthUser();
    const [holidayMasters, setHolidayMasters] = useState([]);
    const [holidayDetails, setHolidayDetails] = useState([]);
    const [selectedMaster, setSelectedMaster] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
        holiday_master_id: '',
        date: '',
        description: '',
        number_of_working_days: ''
    });

    useEffect(() => {
        fetchHolidayMasters();
        fetchHolidayDetails();
    }, []);

    useEffect(() => {
        fetchHolidayDetails();
    }, [selectedMaster]);

    const fetchHolidayMasters = async () => {
        try {
            const response = await http.get(`/holiday_masters?login_user=${user.id}`);
            if (response.data.success) {
                setHolidayMasters(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching holiday masters:', error);
        }
    };

    const fetchHolidayDetails = async () => {
        setLoading(true);
        try {
            let url = `/holiday_master_details?login_user=${user.id}`;
            if (selectedMaster) {
                url += `&holiday_master_id=${selectedMaster}`;
            }
            const response = await http.get(url);
            if (response.data.success) {
                setHolidayDetails(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching holiday details:', error);
            toast.error('Error fetching holiday details');
        } finally {
            setLoading(false);
        }
    };

    const openAddModal = () => {
        setEditingItem(null);
        setFormData({ 
            holiday_master_id: selectedMaster || '', 
            date: '',
            description: '',
            number_of_working_days: ''
        });
        setShowModal(true);
    };

    const openEditModal = (item) => {
        setEditingItem(item);
        setFormData({
            holiday_master_id: item.holiday_master_id || '',
            date: item.date ? item.date.split('T')[0] : '',
            description: item.description || '',
            number_of_working_days: item.number_of_working_days || ''
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingItem(null);
        setFormData({ holiday_master_id: '', date: '', description: '', number_of_working_days: '' });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleMasterChange = (selectedOption) => {
        setFormData({
            ...formData,
            holiday_master_id: selectedOption ? selectedOption.value : ''
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.holiday_master_id) {
            toast.error('Please select a holiday type');
            return;
        }
        if (!formData.date) {
            toast.error('Please select a date');
            return;
        }

        setLoading(true);
        try {
            const requestData = {
                ...formData,
                login_user: user.id
            };

            let response;
            if (editingItem) {
                response = await http.put(`/holiday_master_details/${editingItem.id}`, requestData);
            } else {
                response = await http.post('/holiday_master_details', requestData);
            }

            if (response.data.success) {
                toast.success(editingItem ? 'Updated successfully!' : 'Added successfully!');
                closeModal();
                fetchHolidayDetails();
            } else {
                toast.error(response.data.message || 'Failed to save');
            }
        } catch (error) {
            console.error('Error saving:', error);
            toast.error(error.response?.data?.message || 'Error saving record');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this holiday date?')) {
            return;
        }
        
        try {
            const response = await http.delete(`/holiday_master_details/${id}`);
            if (response.data.success) {
                toast.success('Deleted successfully!');
                fetchHolidayDetails();
            } else {
                toast.error(response.data.message || 'Failed to delete');
            }
        } catch (error) {
            console.error('Error deleting:', error);
            toast.error('Error deleting record');
        }
    };

    const masterOptions = holidayMasters.map(m => ({
        value: m.id,
        label: `${m.name} (${m.number_leave || 0} days)`
    }));

    // Group details by holiday master
    const groupedDetails = holidayDetails.reduce((acc, detail) => {
        const masterName = detail.holidayMaster?.name || detail.holiday_master_name || 'Unknown';
        if (!acc[masterName]) {
            acc[masterName] = [];
        }
        acc[masterName].push(detail);
        return acc;
    }, {});

    return (
        <>
            <Toaster />
            <div className="container-fluid mt-2 px-3">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">Holiday Dates</h5>
                    <button className="btn btn-sm btn-primary" onClick={openAddModal}>
                        <i className="fa fa-plus me-1"></i>Add Date
                    </button>
                </div>

                {/* Filter */}
                <div className="card mb-2">
                    <div className="card-body py-2">
                        <div className="row g-2 align-items-end">
                            <div className="col-md-4">
                                <label className="form-label small mb-1">Holiday Type</label>
                                <Select
                                    options={[{ value: '', label: 'All Types' }, ...masterOptions]}
                                    onChange={(opt) => setSelectedMaster(opt && opt.value ? opt.value : null)}
                                    value={masterOptions.find(opt => opt.value === selectedMaster) || { value: '', label: 'All Types' }}
                                    placeholder="Filter by type"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{
                                        control: (base) => ({ ...base, minHeight: '34px', fontSize: '13px' }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </div>
                            <div className="col-md-2">
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => setSelectedMaster(null)}>
                                    Clear
                                </button>
                            </div>
                            <div className="col-md-6 text-end">
                                <span className="badge bg-secondary">{holidayDetails.length} dates</span>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Table */}
                <div className="card">
                    <div className="card-body p-0">
                        {loading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : holidayDetails.length === 0 ? (
                            <div className="text-center py-4">
                                <p className="text-muted">No holiday dates found</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover table-sm mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>#</th>
                                            <th>Holiday Type</th>
                                            <th>Date</th>
                                            <th>Day</th>
                                            <th>Description</th>
                                            <th>Working Days</th>
                                            <th style={{ textAlign: 'center' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {holidayDetails.map((item, index) => (
                                            <tr key={item.id}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    <span className="badge bg-primary" style={{ fontSize: '10px' }}>
                                                        {item.holidayMaster?.name || item.holiday_master_name || '-'}
                                                    </span>
                                                </td>
                                                <td style={{ fontSize: '13px' }}>
                                                    <strong>{item.date ? moment(item.date).format('DD MMM YYYY') : '-'}</strong>
                                                </td>
                                                <td style={{ fontSize: '12px' }}>
                                                    {item.date ? moment(item.date).format('dddd') : '-'}
                                                </td>
                                                <td style={{ fontSize: '12px' }}>
                                                    {item.description || '-'}
                                                </td>
                                                <td style={{ fontSize: '12px' }}>
                                                    {item.number_of_working_days || '-'}
                                                </td>
                                                <td className="text-center">
                                                    <div className="btn-group btn-group-sm">
                                                        <button 
                                                            className="btn btn-outline-primary"
                                                            onClick={() => openEditModal(item)}
                                                            title="Edit"
                                                        >
                                                            <i className="fa fa-edit"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-outline-danger"
                                                            onClick={() => handleDelete(item.id)}
                                                            title="Delete"
                                                        >
                                                            <i className="fa fa-trash"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                {/* Grouped View */}
                {!selectedMaster && Object.keys(groupedDetails).length > 0 && (
                    <div className="card mt-3">
                        <div className="card-header py-2" style={{ background: '#f8f9fa' }}>
                            <h6 className="mb-0">Grouped by Type</h6>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                {Object.entries(groupedDetails).map(([masterName, details]) => (
                                    <div key={masterName} className="col-md-4 mb-3">
                                        <div className="card h-100">
                                            <div className="card-header bg-light py-2">
                                                <h6 className="mb-0">{masterName}</h6>
                                            </div>
                                            <div className="card-body py-2">
                                                <ul className="list-unstyled mb-0">
                                                    {details.map(detail => (
                                                        <li key={detail.id} className="mb-1" style={{ fontSize: '12px' }}>
                                                            <i className="fa fa-calendar text-primary me-2"></i>
                                                            {moment(detail.date).format('DD MMM YYYY')}
                                                            <small className="text-muted ms-1">
                                                                ({moment(detail.date).format('ddd')})
                                                            </small>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <div className="card-footer bg-light py-1">
                                                <small className="text-muted">
                                                    Total: {details.length} date(s)
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header" style={{ background: '#f8f9fa' }}>
                                <h6 className="modal-title">
                                    {editingItem ? 'Edit' : 'Add'} Holiday Date
                                </h6>
                                <button type="button" className="btn-close" onClick={closeModal}></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Holiday Type <span className="text-danger">*</span></label>
                                        <Select
                                            options={masterOptions}
                                            onChange={handleMasterChange}
                                            value={masterOptions.find(opt => opt.value === formData.holiday_master_id) || null}
                                            placeholder="Select holiday type"
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            styles={{ 
                                                menuPortal: (base) => ({ ...base, zIndex: 9999 }) 
                                            }}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Date <span className="text-danger">*</span></label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            name="date"
                                            value={formData.date}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Description</label>
                                        <textarea
                                            className="form-control"
                                            name="description"
                                            value={formData.description}
                                            onChange={handleChange}
                                            rows="2"
                                            placeholder="Enter description (optional)"
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Number of Working Days</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            name="number_of_working_days"
                                            value={formData.number_of_working_days}
                                            onChange={handleChange}
                                            placeholder="Enter number of working days"
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer py-2">
                                    <button type="button" className="btn btn-sm btn-secondary" onClick={closeModal}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-sm btn-primary" disabled={loading}>
                                        {loading ? 'Saving...' : (editingItem ? 'Update' : 'Save')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
